import {
  dateLocale,
  formatDateRange,
  monthLong,
  monthShort,
  parseDateOnly,
  toDateOnly,
} from '@voll-intelligence/dates';
import { genUiT, type GenUiKey } from '@voll-intelligence/genui-contract';

/*
 * Os formatadores puros dos cards de voo moram no intelligence-ui-kit (#4997),
 * que os resultados de voo do kit também usam. Este módulo reexporta os de lá,
 * para os cards do chat não mudarem de import, e guarda só os que dependem do
 * catálogo de textos (`genUiT`) ou que só o chat usa.
 */
export {
  arrivalDayOffset,
  fmtDate,
  fmtDayMonth,
  fmtDuration,
  fmtMoney,
  fmtMoneyWhole,
  fmtTime,
  fmtWeekdayDate,
  fmtWeekdayDayMonth,
} from '@voll-intelligence/intelligence-ui-kit';

type LocaleInput = string | null | undefined;

/**
 * O dia de HOJE em `YYYY-MM-DD`, no fuso de quem está olhando.
 *
 * **O fuso é o do RUNTIME (o relógio do navegador), e a escolha é deliberada.** É o
 * mesmo relógio que a pessoa vê na barra do sistema, e "hoje" numa grade de datas é
 * afirmação sobre o dia dela — não sobre o dia de um escritório. A alternativa, fixar
 * America/Sao_Paulo, erraria para o viajante que abre o card em outro país, e é o tipo
 * de acerto que só aparece em produção.
 *
 * `new Date().toISOString().slice(0, 10)` NÃO serve: `toISOString` é UTC. Em
 * America/Sao_Paulo (UTC-3), das 21h à meia-noite ele já devolve o dia seguinte —
 * media-se `2026-08-05T22:00` local virando `"2026-08-06"`. No calendário do planner
 * isso punha o anel de "hoje" na célula de amanhã E fazia o piso da grade tratar a
 * data de hoje como passado (cinza, impickável) por três horas toda noite.
 *
 * `toDateOnly` (módulo de datas do intelligence-ui-kit) lê pelos getters locais, que
 * já estão no fuso do runtime sem depender de engine.
 */
export function localDayIso(): string {
  return toDateOnly(new Date());
}

/**
 * Texto do "+N" de chegada em dia seguinte, para tooltip e rótulo acessível.
 * Vazio quando a chegada é no mesmo dia.
 *
 * Vive aqui porque **três** linhas de trecho mostram o mesmo glifo (oferta,
 * resumo do drawer e detalhe), e o glifo sozinho é lido como "mais um" — sem o
 * texto o leitor de tela perde a informação que muda diária de hotel e hora de
 * reunião. Com o rótulo em cópia local isso já falhou: uma das três linhas
 * ficou sem ele.
 */
export function nextDayArrivalLabel(dayOffset: number, locale: LocaleInput = 'pt-BR'): string {
  if (dayOffset <= 0) return '';
  return genUiT(
    locale,
    dayOffset === 1 ? 'flight.segment.arrivesNextDay_one' : 'flight.segment.arrivesNextDay_other',
    { count: dayOffset },
  );
}

/**
 * Subtítulo do planner por extenso, locale-aware. Data(s) ISO 'YYYY-MM-DD'.
 * Sem ano, exceto quando `out`/`ret` diferem de ano (aí acrescenta o ano nas
 * DUAS pontas). Formas:
 *  - só ida: pt "10 de agosto" · en "August 10" · es "10 de agosto"
 *  - ida-volta mesmo mês: pt "10 a 18 de agosto" · en "August 10–18" · es "del 10 al 18 de agosto"
 *  - ida-volta meses difs: pt "28 de agosto a 3 de setembro" · en "Aug 28 – Sep 3"
 * `out` inválido → vazio; `ret` inválido → trata como só ida.
 */
export function fmtTripDates(out: string, ret: string | undefined, locale: LocaleInput): string {
  const o = parseDateOnly(out);
  if (!o) return '';
  // Qual forma do mês entra em cada frase é decisão do CHAMADOR, não do
  // template: en usa o nome curto nas frases de DOIS meses (hábito de hoje),
  // pt/es sempre o longo. Assim os quatro templates compartilham o mesmo
  // conjunto de placeholders nos três idiomas (o que `i18n.test.ts` cobra).
  const twoMonth = (date: Date) =>
    dateLocale(locale) === 'en' ? monthShort(date, locale) : monthLong(date, locale);
  const tt = (key: GenUiKey, values: Record<string, string | number>) =>
    genUiT(dateLocale(locale), key, values);

  const r = ret ? parseDateOnly(ret) : null;
  if (!r) return tt('tripDates.oneWay', { d: o.getDate(), month: monthLong(o, locale) });
  if (o.getFullYear() !== r.getFullYear()) {
    return tt('tripDates.crossYear', {
      d1: o.getDate(),
      month1: twoMonth(o),
      y1: o.getFullYear(),
      d2: r.getDate(),
      month2: twoMonth(r),
      y2: r.getFullYear(),
    });
  }
  if (o.getMonth() === r.getMonth()) {
    return tt('tripDates.sameMonth', {
      d1: o.getDate(),
      d2: r.getDate(),
      month: monthLong(o, locale),
    });
  }
  return tt('tripDates.crossMonth', {
    d1: o.getDate(),
    month1: twoMonth(o),
    d2: r.getDate(),
    month2: twoMonth(r),
  });
}

/**
 * O vão de datas curto do planner, na MESMA gramática da pill de resumo
 * (`FlightSummaryPill` do intelligence-ui-kit): "17 jun"
 * (só ida, ou volta no mesmo dia), "20–22 jun" (mesmo mês e ano), "29 jun – 2
 * jul" (meses diferentes). Fora do ano corrente o ano entra por extenso só na
 * última data ("28 dez – 3 jan 2027"). Vazio quando a ida não é uma data;
 * volta inválida vira só ida. Não substitui `fmtTripDates`: aquele é o
 * subtítulo POR EXTENSO do planner, este é o resumo curto de uma barra.
 */
export function fmtTripRange(
  out: string,
  ret: string | undefined,
  locale: LocaleInput = 'pt-BR',
): string {
  const start = parseDateOnly(out);
  if (!start) return '';
  return formatDateRange(start, ret ? parseDateOnly(ret) : null, locale, new Date());
}
